import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "../Api";
import { IMAGE_URL, ASSETS_URL } from "../utils/constants";

export default function Sidebar() {
  const { pathname } = useLocation();
  const [permissions, setPermissions] = useState([]);
  const [reportCount, setReportCount] = useState(0);
  const [OneWordReportCount, setOneWordReportCount] = useState(0);
  const [CalculationReportCount, setCalculationReportCount] = useState(0);
  const [BlackboxReportCount, setBlackboxReportCount] = useState(0);
  const [ImageReportCount, setImageGameReportCount] = useState(0);
  const [encyclopaediaReportCount, setEncyclopaediaGameReportCount] = useState(0);
  const [triviaReportCount, setTriviaGameReportCount] = useState(0);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const user = useSelector((state) => state?.user?.user?.user_details);

  useEffect(() => {
    const fetchPermission = async () => {
      try {
        const response = await api.get("/user-service/user/admin/permission");
        console.log("sidebar permission API Response:", response.data);
        setPermissions(response.data.data || []);
      } catch (error) {
        console.error("API Error at sidebar:", error.message);
      }
    };

    if (user && user?.user_type !== "admin") {
      fetchPermission();
    }
  }, [user]);

  // Fetch report and question counts
  useEffect(() => {
    const endpoints = [
      { url: "/quiz/admin/check-report-unsolved", setter: setReportCount },
      { url: "/one-word/admin/check-report-unsolved", setter: setOneWordReportCount },
      { url: "/calculation/admin/check-report-unsolved", setter: setCalculationReportCount },
      { url: "/blackbox/admin/check-report-unsolved", setter: setBlackboxReportCount },
      { url: "/image-game/admin/check-report-unsolved", setter: setImageGameReportCount },
      { url: "/trivia/admin/check-report-unsolved", setter: setTriviaGameReportCount },
      { url: "/encyclopaedia/admin/check-report-unsolved", setter: setEncyclopaediaGameReportCount },
    ];

    const fetchAllCounts = async () => {
      console.log("Fetching all counts...");
      try {
        const responses = await Promise.all(
          endpoints.map(({ url }) =>
            api.get(url).catch(error => {
              console.error(`Error fetching ${url}:`, error.message);
              return null;
            })
          )
        );

        responses.forEach((response, index) => {
          if (response?.data?.count) {
            endpoints[index].setter(response.data.count);
          }
        });
      } catch (error) {
        console.error("Error fetching counts:", error.message);
      }
    };

    // Initial fetch
    fetchAllCounts();

    // Refresh all counts every 30 seconds
    const interval = setInterval(() => {
      fetchAllCounts();
    }, 30000);

    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    const fetchLLMConfig = async () => {
      try {
        const response = await api.get("/ai-engine/admin/config/llm");
        console.log("LLM Config API Response:", response.data);
        if (response.data) {
          setIsSidebarCollapsed(response.data.open_ai)
        }
      }
      catch (error) {
        console.error("Error fetching LLM config:", error.message);
      }

    }

    fetchLLMConfig();

  }, [isLoading]);


  // Handle toggle with API call
  const handleToggleClick = async () => {
    const newValue = !isSidebarCollapsed;
    setIsLoading(true);
    try {
      const response = await api.post(
        "/ai-engine/admin/config/llm",
        { open_ai: newValue, claude: true }
      );
      console.log("Toggle API Response:", response.data);
      setIsSidebarCollapsed(newValue);
    } catch (error) {
      console.error("Error calling toggle API:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Permission checker
  const hasPermission = (permissionKey) => {
    if (user?.user_type === "admin") return true;
    const permissionKeys = Array.isArray(permissionKey)
      ? permissionKey
      : [permissionKey];

    return permissionKeys.some((key) =>
      permissions.some((perm) => perm.key === key)
    );
  };

  // Static menu configuration
  const menuItems = [
    {
      path: "/user",
      icon: IMAGE_URL + "user-mng-icon.svg",
      alt: "User Management",
      text: "User Management",
      permissionKey: "user_management",
      count: null,
    },
    {
      path: "/subscriptions",
      icon: IMAGE_URL + "receipt-dollar-stroke-rounded.svg",
      alt: "Subscription Plans",
      text: "Subscription Plans",
      permissionKey: ["subscription_management", "user_management"],
      activePaths: ["/subscriptions"],
      count: null,
    },
    {
      path: "/subscription-add-ons",
      icon: IMAGE_URL + "receipt-dollar-stroke-rounded.svg",
      alt: "Add-on Packs",
      text: "Add-on Packs",
      permissionKey: ["subscription_management", "user_management"],
      activePaths: ["/subscription-add-ons"],
      count: null,
    },
    {
      path: "/report-select-game",
      icon: IMAGE_URL + "Report.svg",
      alt: "Report Management",
      text: "Reported Questions",
      permissionKey: "report_management",
      count: reportCount + OneWordReportCount + CalculationReportCount + BlackboxReportCount + ImageReportCount + encyclopaediaReportCount + triviaReportCount,
    },
    {
      path: "/manufacturer",
      icon: IMAGE_URL + "airplane.svg",
      alt: "Manufacturer Management",
      text: "Manufacturers",
      permissionKey: "manufacturers",
      count: null,
    },
    {
      path: "/aircraft",
      icon: IMAGE_URL + "Frame (17).svg",
      alt: "Aircraft Management",
      text: "Aircrafts",
      permissionKey: "aircrafts",
      count: null,
    },
    {
      path: "/glossary",
      icon: ASSETS_URL + "glos.svg",
      alt: "Glossary Management",
      text: "Glossary",
      permissionKey: "aircrafts",
      count: null,
    },
    {
      path: "/airport",
      icon: ASSETS_URL + "aort.svg",
      alt: "Airport Management",
      text: "Airport",
      permissionKey: "aircrafts",
      count: null,
    },
    {
      path: "/sub-admin",
      icon: IMAGE_URL + "user-mng-icon.svg",
      alt: "SubAdmin Management",
      text: "SubAdmin",
      permissionKey: "subadmin",
      activePaths: ["/sub-admin", "/add-sub-admin"],
      count: null,
    },
    {
      path: "/content",
      icon: IMAGE_URL + "content.png",
      alt: "Content Management",
      text: "Content Management",
      permissionKey: "content_management",
    },

  ];

  // Dynamic nested game paths
  const gameMenu = [
    {
      key: "quiz-game",
      label: "Quiz Game",
      base: "quiz-game",
    },
    {
      key: "one-word-game",
      label: "One Word Game",
      base: "one-word-game",
    },
    {
      key: "calculation-game",
      label: "Calculation Game",
      base: "calculation-game",
    },
    {
      key: "black-box-game",
      label: "Black Box Game",
      base: "black-box-game",
    },
  ];


  const gameManu2 = [
    {
      key: "trivia",
      label: "Jetting Around The World"
    },
    {
      key: "image-game",
      label: "Plane spotter"
    },
    {
      key: "encyclopaedia",
      label: "Citius. Altius. Longius."
    }
  ]


  const IngestionMenu = [
    {
      key: "upload-manufacturers",
      label: "Manufacturers"
    },
    {
      key: "upload-airports",
      label: "Airports"
    },
    {
      key: "upload-aircraft",
      label: "Aircrafts"
    },
    {
      key: "upload-glossary",
      label: "Glossary"
    },
    {
      key: "upload-aviation-chronicle",
      label: "Aviation Chronicle"
    },

  ]


  const getGameBase = (base) => `/game-management/${base}`;
  const getIngestionBase = (key) => `/${key}`;


  // Filtered menu based on permission
  const filteredMenuItems = menuItems.filter((item) =>
    hasPermission(item.permissionKey)
  );

  // Active state handler for regular menu
  const isActivePath = (item) => {
    if (item.activePaths) {
      return item.activePaths.includes(pathname);
    }
    return pathname === item.path;
  };

  // Check if any game menu matches active path
  const isGameMenuActive =
    gameMenu.some((game) => pathname.startsWith(getGameBase(game.base))) ||
    gameManu2.some((game) => pathname.startsWith(`/game-question-set/${game.key}`));

  const isAnyGameActive = isGameMenuActive;


  // check ingestion active

  const isIngestionMenuActive = IngestionMenu.some((ingestion) => pathname.startsWith(getIngestionBase(ingestion.key)));

  const isIngestionActive = isIngestionMenuActive;

  return (
    <>
      {user && (
        <div className="side-navbar">
          {/* <button className="opnBtn" type="button">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.59877 14.8186C9.59877 14.8186 4.77735 11.2677 4.77734 9.99721C4.77734 8.7266 9.59877 5.17578 9.59877 5.17578"
                stroke="#707070"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15.2238 14.8186C15.2238 14.8186 10.4023 11.2677 10.4023 9.99721C10.4023 8.7266 15.2238 5.17578 15.2238 5.17578"
                stroke="#707070"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button> */}

          <div className="sidebar-logo">
            <a className="logo" href="/">
              <img src={ASSETS_URL + "new_logo.png"} alt="Logo" />
            </a>
          </div>

          <div className="side_menu_wrapper">
            <div className="sidemenu_list">
              {/* <span className="nvx">
                <span className="nvx_text">PAGES</span>
                <span className="nvx_dots">...</span>
              </span> */}

              <ul className="nav flex-column position-relative">
                {filteredMenuItems.map((item) => (
                  <li key={item.path} className="sidebar-link">
                    <Link
                      className={
                        isActivePath(item) ? "nav-link position-relative active" : "nav-link position-relative"
                      }
                      to={item.path}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <span className="menu-icon">
                          <img src={item.icon} alt={item.alt} />
                        </span>
                        <span className="menu_text">{item.text}</span>
                      </span>
                      {item.count !== null && item.count > 0 && (
                        <span className="report_badge"
                          style={{
                            backgroundColor: '#ff6b6b',
                            color: 'white',
                            borderRadius: '12px',
                            padding: '2px 8px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            marginLeft: '3px'
                          }}
                        >
                          {item.count}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}

                {/* GAME MANAGEMENT */}
                {hasPermission("game_management") &&
                  <button
                    className={`nav-link d-flex position-relative text-white fw-semibold ${isAnyGameActive ? "active" : ""
                      }`}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseGame"
                    aria-expanded={isAnyGameActive}
                  >
                    <span className="menu-icon">
                      <i className="fa-solid fa-trophy"></i>
                    </span>
                    <span className="menu_text">Game Management</span>
                  </button>
                }


                <div
                  className={`collapse ${isGameMenuActive ? "show" : ""}`}
                  id="collapseGame"
                >
                  <div className="card card-body inner_nav">
                    <ul className="nav flex-column position-relative">
                      {gameMenu.map((game) => {
                        const base = getGameBase(game.base);
                        const active = pathname.startsWith(base);

                        return (
                          <li key={game.key} className="sidebar-link">
                            <Link
                              className={
                                active ? "nav-link active" : "nav-link"
                              }
                              to={base}
                            >
                              <span className="menu_text">{game.label}</span>
                            </Link>
                          </li>
                        );
                      })}

                      {gameManu2.map((game) => {
                        const route = `/game-question-set/${game.key}`
                        const active = pathname.startsWith(route);


                        return (
                          <li key={game.key} className="sidebar-link">
                            <Link
                              className={
                                active ? "nav-link active" : "nav-link"
                              }
                              to={route}
                            >
                              <span className="menu_text">{game.label}</span>
                            </Link>
                          </li>
                        );
                      }
                      )}
                    </ul>
                  </div>
                </div>

                {/* Ingestion  */}


                <button
                  className={`nav-link d-flex position-relative text-white fw-semibold ${isIngestionActive ? "active" : ""
                    }`}
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseIngestion"
                  aria-expanded={isIngestionActive}
                >
                  <span className="menu-icon">
                    <i class="fa-solid fa-file-import"></i>
                  </span>
                  <span className="menu_text">Ingestion</span>
                </button>



                <div
                  className={`collapse ${isIngestionActive ? "show" : ""}`}
                  id="collapseIngestion"
                >
                  <div className="card card-body inner_nav">
                    <ul className="nav flex-column position-relative">
                      {IngestionMenu.map((ingestion) => {
                        const base = getIngestionBase(ingestion.key);
                        const active = pathname.startsWith(base);

                        return (
                          <li key={ingestion.key} className="sidebar-link">
                            <Link
                              className={
                                active ? "nav-link active" : "nav-link"
                              }
                              to={base}
                            >
                              <span className="menu_text">{ingestion.label}</span>
                            </Link>
                          </li>
                        );
                      })}

                    </ul>
                  </div>
                </div>



                {/* end Ingestion */}
              </ul>
            </div>

            {/* Toggle Button at bottom of sidebar */}
            <div className="sidebar-toggle-container" style={{
              padding: "15px",
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              display: "flex",
              justifyContent: "center"
            }}>
              <button
                className="sidebar-toggle-btn"
                onClick={handleToggleClick}
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  color: "white",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "16px",
                  transition: "all 0.3s ease",
                  fontWeight: "500",
                  flexWrap: "wrap",
                  justifyContent: "center"
                }}
                title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
              >
                <i className={`fa-solid ${isSidebarCollapsed ? "fa-toggle-on" : "fa-toggle-off"}`}></i>
                <span>{isSidebarCollapsed ? "Open AI On" : "Claude AI On"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
